<template>
  <div id="smartText" class="page white" refs="smartText">
    <el-container class="content-aside">
      <el-main class="cpl-main">
        <el-row class="options" :gutter="20"></el-row>

        <smart-text-tags-slider
          :tagsProp="tagsSummary"
          :selectedUserDbIdProp="selectedUserDbId"
          @smart-text-tags-slider-change="handleTagsSliderChange($event)">
        </smart-text-tags-slider>

        <!-- User Db Listing -->
        <perfect-scrollbar v-if="!selectedUserDbId" id="userDatabaseListContent" class="main-content-row cstm-ps-scrollbar-vertical">
          <div v-for="(item, indx) in userDbList" :key="indx" :class="['user-db-item']" :gutter="20">
            <div class="smart-text-preview">
              <div class="smart-text-icon"></div>
            </div>

            <div class="center-content">
              <div class="db-name-wrapper"><div class="txt-label">{{item.name}}</div></div>
            </div>

            <div v-if="item.tags" class="db-tags-wrapper">
              <template v-for="(tag, indx) in item.tags"                   :key="`user-db-tag-${indx}`">
                <el-tag
                  :closable="false"
                  :disable-transitions="true"
                  :style="{'backgroundColor': $utils.getBackgroundColor({ indx })}"
                  :class="['user-db-tag']">
                  <div class="tag-val"><span>{{tag}}</span></div>
                </el-tag>
              </template>
            </div>

            <div v-if="enableUserDbActions" class="db-actions-wrapper">
              <div class="actions">
                <el-button :class="['cstm-icon-btn-medium copy', {}]"
                  @click="handleUserDbItemClick(userDbActionOptions.copy, item, indx, $event)">
                </el-button>

                <el-button :class="['cstm-icon-btn-medium remove', {}]"
                  @click="handleUserDbItemClick(userDbActionOptions.delete, item, indx, $event)">
                </el-button>
              </div>
            </div>

            <div @click="handleUserDbItemClick(userDbActionOptions.entireRowClicked, item, indx, $event)" class="user-db-item-cover"></div>
          </div>
        </perfect-scrollbar>

        <!-- Selected User DB Details -->
        <div id="userDatabaseDetails" v-if="selectedUserDbDetails" :gutter="20" :class="['main-content-row user-database-details-row']">
          <div :class="['user-db-item', 'as-header']" :gutter="20">
            <div class="smart-text-preview">
              <div class="smart-text-icon"></div>
            </div>

            <div class="center-content">
              <div class="db-name-wrapper"><div class="txt-label">{{selectedUserDbDetails.name}}</div></div>
            </div>

            <div class="user-db-item-cover"></div>
          </div>

          <div class="database-details-content hide-scrollbar">
            <div id="userDbRowSortGroupWrapper">
              <draggable
                v-if="selectedUserDbDetails"
                v-bind="sortDraggableOptions"
                v-model="selectedUserDbDetails._edit.data"
                id="userDbRowSortGroup"
                tag="span"
                :disabled="false"
                :item-key="(item) => item?._edit?.sortKeyWithIndx"
                :component-data="{name:'flip-list'}"
                :class="[{'user-db-sortable-group': true, 'disabled': false}]"
                :forceFallback="true"
                @start="handleRowItemSortStart"
                @end="handleRowItemSortEnd">

                <template #item="{ element: item, index: rowIndx }">
                  <div
                    :class="['row-item', 'sortable-el', 'list-group-item', {
                    'is-row-selected': (item._edit.isRowSelected),
                    'is-sort-drag': rowItemSortDragTriggered,
                    'mouse-over': (item._edit.mouseEvent && item._edit.mouseEvent === 'mouseover'),
                    'mouse-out': (item._edit.mouseEvent && item._edit.mouseEvent === 'mouseout')}]"
                    @mouseover="handleRowItemMouseHoverAndMouseOut('mouseover', item, $event)"
                    @mouseout="handleRowItemMouseHoverAndMouseOut('mouseout', item, $event)">

                    <div class="user-db-row-item-actions-visibility-flag">
                      <el-button :class="['cstm-icon-btn-medium', {'eyes_on': item._edit.visible, 'eyes_off': !item._edit.visible}]"
                        @click="handleSelectedUserDbRowClick(userDbActionOptions.rowVisible, item, rowIndx, $event)">
                      </el-button>
                    </div>

                    <!--Columns -->
                    <div :class="['usr-db-columns', {'more-than-one': item._edit.columns.length > 1 }]">
                      <div v-for="(column, ColIndx) in item._edit.columns"
                        :key="ColIndx"
                        :class="['cell', `row-${rowIndx}-col-${ColIndx}`, `type-${column.type.replace(/ /g, '-')}`,
                        {'active': ((selectedCell.row && selectedCell.column) && (selectedCell.row.rowIndex === rowIndx && selectedCell.column.index === column.index)),
                        'fade': ((selectedCell.row && selectedCell.column) && (selectedCell.row.rowIndex === rowIndx && selectedCell.column.index !== column.index))}]"
                        :style="{width: column.width}">

                        <div :class="['cell-content', {'red-text': column.redText}]"
                          @click.capture="handleSelectedUserDbRowClick(userDbActionOptions.selectedUserDbRowCellClick, item, rowIndx, $event, column, ColIndx, {targetInput: `row-${rowIndx}-col-${ColIndx}-input`})">
                          <div class="col-text">{{column.title}}</div>
                          <el-input
                            v-if="column.type === 'text'"
                            :ref="`row-${rowIndx}-col-${ColIndx}-input`"
                            :class="['cstm-input-grey', 'column-editable-text']"
                            :model-value="column.title"
                            :maxlength="column.maxInputValue ? parseInt(column.maxInputValue) : null"
                            @update:model-value="handleColumnInputTyping(item, rowIndx, column.columnName, $event, ColIndx)">
                          </el-input>
                        </div>
                      </div>
                    </div>

                    <div :style="{'backgroundColor': item._edit.backgroundColor}" class="user-db-row-item-background"></div>

                    <!-- Row Actions -->
                    <div  class="user-db-row-item-actions">
                      <!-- Duplicate Row -->
                      <el-button :class="['cstm-icon-btn-medium copy', {'hidden': false,'is-selected': false}]"
                        @click="handleSelectedUserDbRowClick(userDbActionOptions.selectedUserDbCopyRow, item, rowIndx, $event)">
                      </el-button>

                      <!-- Delete Row -->
                      <el-button :class="['cstm-icon-btn-medium remove', {'is-parent-row-selected': false,
                        'is-selected': false,
                        'hover-all-delete-icon': hoverAllDeleteIcon}]"
                        @click="handleSelectedUserDbRowClick(userDbActionOptions.selectedUserDbDeleteRow, item, rowIndx, $event)">
                      </el-button>
                    </div>

                    <div class="user-db-row-item-cover">
                      <div v-for="(col, ColIndx) in item._edit.columns"
                        :key="ColIndx"
                        :class="['cell-click-ref', `row-${rowIndx}-col-${ColIndx}`]"
                        :style="{width: col.width}"
                        @click="handleSelectedUserDbRowClick(userDbActionOptions.selectedUserDbEntireRowClick, item, rowIndx, $event, col, ColIndx, {targetInput: `row-${rowIndx}-col-${ColIndx}-input`})">
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>
            </div>

            <!--Add new Row-->
            <el-button @click="handleAddNewRow" class="cstm-icon-btn-medium add add-new-user-db-row"></el-button>
          </div>
        </div>
      </el-main>

      <el-aside class="cpl-aside">
        <div class="content hide-scrollbar">
          <div class="right-side-menu">
            <user-db-menu v-if="selectedUserDbDetails"
            ref="userDbRowMenuForm"
            :selectedUserDbDetailsProp="selectedUserDbDetails"
            :userDbRowProp="selectedUserDbRow"
            :selectedCellProp="selectedCell"
            :defaultNumberOfColumnsProp="defaultNumberOfColumns"
            @user-db-menu-change="handleUserDbMenuChange($event)"></user-db-menu>
          </div>
        </div>
      </el-aside>
    </el-container>
  </div>
</template>

<script src="./smart-text.js"></script>
<style lang="scss" src="./smart-text.scss"></style>
